import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { type VoiceShort } from '@intone/shared/types';
import { Button } from '@/components/ui/button';
import { EntityList } from '@/widgets/entityList';

import { useShowDialog, UnsavedChangesDialogTexts } from '@/hooks/useShowDialog';
import { useDeleteVoice, useCloneVoice } from '@/entities/voice/model/useVoices';

import { useUnsavedChanges } from '../lib/FormStatusProvider';

type VoiceListProps = {
  voiceItems: VoiceShort[];
  isLoading: boolean;
}

export function VoiceList({ voiceItems, isLoading }: VoiceListProps) {
  const { isDirty, setIsDirty } = useUnsavedChanges();
  const navigate = useNavigate();
  const { id: currentId } = useParams<{ id?: string }>();

  const activeId = currentId ? Number(currentId) : null;
  const deleteVoice = useDeleteVoice();
  const cloneVoice = useCloneVoice();

  const sortedVoices = useMemo(() => {
    return [...voiceItems].sort((a, b) => {
      if (a.company_id !== b.company_id) {
        return a.company_id ? 1 : -1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [voiceItems]);

  const shouldBlock = isDirty && !deleteVoice.isPending;
  const { showConfirmDialog } = useShowDialog(shouldBlock);

  const handleSelectItem = (item: VoiceShort) => {
    if (isDirty) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.default)) return;
    }
    setIsDirty(false);
    navigate(`/voices/${item.id}`);
  };

  const handleCreateNew = () => {
    if (isDirty) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.default)) return;
    }
    setIsDirty(false);
    navigate('/voices/new', { replace: true });
  };

  const handleCloneVoice = (item: VoiceShort) => {
    if (isDirty) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.default)) return;
    }
    setIsDirty(false);

    cloneVoice.mutate(item.id, {
      onSuccess: (newVoice) => {
        toast.success('Voice cloned successfully', { id: 'clone-voice-success' });
        navigate(`/voices/${newVoice.id}`);
      },
      onError: (error: Error) => {
        toast.error(`Failed to clone voice${error.message ? ': ' + error.message : ''}`, { id: 'clone-voice-error' });
      },
    });
  };

  const handleDeleteVoice = (item: VoiceShort) => {
    if (!window.confirm('Are you sure you want to delete this voice? This action cannot be undone.?')) return;

    if (activeId === item.id) {
      navigate('/voices', { replace: true });
    }

    deleteVoice.mutate(item.id, {
      onSuccess: () => {
        toast.success('Voice deleted successfully', { id: 'delete-voice-success' });
      },
      onError: (error: Error) => {
        toast.error(`Failed to delete voice${error.message ? ': ' + error.message : ''}`, { id: 'delete-voice-error' });
        if (activeId === item.id) {
          navigate(`/voices/${item.id}`, { replace: true });
        }
      },
    });
  };

  const createButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCreateNew}
      disabled={isLoading || deleteVoice.isPending}
      className="w-full"
    >
      <Plus className="w-3 h-3 mr-2" />
      Create New Voice
    </Button>
  );

  return (
    <EntityList<VoiceShort>
      items={sortedVoices}
      selectedId={activeId ?? undefined}
      getId={(v) => v.id}
      onSelect={handleSelectItem}
      onDuplicate={handleCloneVoice}
      onDelete={handleDeleteVoice}
      getCanDelete={(v) => v.company_id !== null}
      renderPrimary={(v) => (
        <span className={v.company_id === null ? 'opacity-50' : ''}>
          {v.name} ({v.alt_key})
        </span>
      )}
      searchPlaceholder="Search voices..."
      searchFilter={(v, q) =>
        v.name.toLowerCase().includes(q) ||
        v.id.toString().includes(q) ||
        v.alt_key.toLowerCase().includes(q)
      }
      isLoading={isLoading}
      emptyMessage="No voices found"
      createButton={createButton}
    />
  );
}